import { MONEY_SCALE, PERCENT_SCALE, compare, fromString, isPositive, round, toString, zero, } from '@aeris/money';
/**
 * Payments.
 *
 * Master Specification section 21 is four lines long and states the whole
 * design:
 *
 *   > Criar abstração de PaymentMethod/PaymentProvider.
 *   > Métodos possíveis: Cash, PIX, Card, BankSlip, CustomerCredit,
 *   >   BankTransfer, TEF, Gateway.
 *   > Uma venda pode possuir pagamento misto.
 *
 * Two tables come out of that, and they are deliberately different in kind:
 *
 *   * `payment_method` is a **catalogue** — what a tenant accepts, at what fee,
 *     settling when. It is edited, archived, reordered; it is configuration.
 *
 *   * `payment` is a **ledger** — money that actually moved. Append-only, and
 *     append-only by privilege: `aeris_app` holds `select, insert` and nothing
 *     else (migration 0012). A refund is a new row that names the receipt it
 *     reverses; it is never an edit. A payment that can be edited is a payment
 *     that can be made to have never happened, and every dispute a shopkeeper
 *     ever has with a till turns on exactly that.
 *
 * Every amount here is a `Decimal` from `@aeris/money` — never a JavaScript
 * `number`, at any layer, including the JSON the API returns. ADR-0016.
 */
// ---------------------------------------------------------------------------
// Method kinds
// ---------------------------------------------------------------------------
/**
 * The fixed set from section 21.
 *
 * `Card` is split into `card_credit` and `card_debit` because in Brazil they
 * are not one instrument wearing two hats: débito settles in a day and cannot
 * be instalment-based, crédito settles around thirty and usually is, and the
 * MDR differs by several percentage points. A single `card` kind would push
 * that difference into a comment on a settings screen.
 *
 * `tef` and `gateway` are channels rather than instruments — a TEF terminal
 * runs a debit or a credit transaction — but they are named separately because
 * what distinguishes them operationally is *who authorises*, which is exactly
 * what a `PaymentProvider` implementation is. See provider.ts.
 *
 * This list is closed. A kind carries behaviour (`allowsChange`,
 * `supportsInstalments`, a settlement default) that has to be decided by
 * somebody, so a free-text kind would be a kind with no behaviour at all.
 */
export const PAYMENT_METHOD_KINDS = [
    /** Notes and coins. The only kind that can give change. */
    'cash',
    /** Instant, irreversible, and settled the moment it lands. */
    'pix',
    'card_credit',
    'card_debit',
    /** Boleto bancário. */
    'bank_slip',
    /** The customer's own balance with the shop — "fiado", store credit. */
    'customer_credit',
    /** TED/DOC, or a transfer the shop simply saw arrive. */
    'bank_transfer',
    /** A card terminal integrated with the till. */
    'tef',
    /** An online acquirer: Mercado Pago, Stone, PagSeguro, Cielo. */
    'gateway',
    /**
     * Um comprovante emitido fora do Aeris — o vale troca (F106).
     *
     * Nem gaveta, nem banco, nem título: é a única forma cujo dinheiro **não
     * existe em lugar nenhum** do ponto de vista do sistema. O cliente devolveu
     * mercadoria noutro dia, saiu com um papel, e hoje ele volta e o papel vale.
     *
     * Por decisão do dono, o Aeris **não emite, não numera e não confere** o
     * vale. Ele aceita. É por isso que não há cadastro de vales: um número
     * guardado aqui prometeria uma conferência que não acontece.
     *
     * Precisa ser um kind próprio porque cada um dos oito acima mente sobre ele
     * num lugar diferente — `cash` põe cédula que não existe na gaveta,
     * `pix`/cartão criam liquidação futura que nunca cai no extrato, e
     * `customer_credit` inverte o sentido: um vale EXTINGUE dívida, não cria.
     */
    'voucher',
];
export const isPaymentMethodKind = (value) => typeof value === 'string' &&
    PAYMENT_METHOD_KINDS.includes(value);
/**
 * Whether a kind can hand money back at the counter.
 *
 * Cash, and nothing else. This is not a preference: a PIX of R$ 100,00 against
 * a bill of R$ 90,00 does not produce R$ 10,00 of change, it produces a
 * customer who has overpaid — and a till that offers change on it will be
 * short by ten reais every time somebody rounds up. Section 21 names troco as
 * a property of the method for this reason.
 *
 * A check constraint in migration 0012 enforces the same rule, so a method row
 * cannot claim otherwise even if this function is never called.
 */
export const allowsChange = (kind) => kind === 'cash';
/**
 * Whether a kind can be split into instalments.
 *
 * Debit, cash, PIX and a bank transfer are single movements by construction:
 * the money is either there or it is not. The rest represent a promise, and a
 * promise can be divided.
 */
export const supportsInstalments = (kind) => {
    switch (kind) {
        case 'card_credit':
        case 'bank_slip':
        case 'customer_credit':
        case 'gateway':
        case 'tef':
            return true;
        // `voucher` entra aqui porque um vale já foi pago inteiro no dia em que foi
        // emitido: parcelar um comprovante de dinheiro passado não quer dizer nada.
        case 'cash':
        case 'pix':
        case 'card_debit':
        case 'bank_transfer':
        case 'voucher':
            return false;
    }
};
/**
 * What a newly created method of this kind starts as.
 *
 * **Defaults, not rules.** The numbers are the ordinary Brazilian arrangement
 * — débito em D+1, crédito em 30 dias, boleto compensado no dia seguinte — and
 * every one of them is negotiated per acquirer and per contract. A shop with a
 * 14-day credit arrangement edits the method and the edit sticks; nothing in
 * the product reads this function again afterwards.
 *
 * They live here rather than in the migration's DEFAULT clauses because a
 * default that is only in the schema cannot be shown on the screen that is
 * about to save it.
 */
export const defaultSettlement = (kind) => {
    switch (kind) {
        // O vale liquida junto com dinheiro e PIX porque não há nada a compensar:
        // sem adquirente, sem banco, sem D+1. Aceitar o papel É o recebimento.
        case 'cash':
        case 'pix':
        case 'voucher':
            return { settlesImmediately: true, settlementDays: 0 };
        case 'card_debit':
        case 'tef':
        case 'bank_slip':
        case 'bank_transfer':
            return { settlesImmediately: false, settlementDays: 1 };
        case 'card_credit':
        case 'gateway':
            return { settlesImmediately: false, settlementDays: 30 };
        case 'customer_credit':
            // The shop has been paid in trust, not in money. It is a receivable from
            // the first second, which is why it never settles immediately even
            // though nothing physically has to clear.
            return { settlesImmediately: false, settlementDays: 30 };
    }
};
/**
 * What a newly created method of this kind starts as, for contas a receber.
 *
 * `customer_credit` and nothing else. Not a preference: `defaultSettlement`
 * above already says it in words — *the shop has been paid in trust, not in
 * money; it is a receivable from the first second*. A fiado sale that raises no
 * título is a debt the system watched being created and chose not to record.
 *
 * Everything else starts false, INCLUDING boleto — not because a boleto is
 * never a receivable, but because whether the shop tracks it as one is a real
 * business choice about when it issues the slip, and defaulting it on would
 * fill a financeiro with debts nobody asked to record. The screen makes it one
 * click either way.
 */
export const defaultGeneratesReceivable = (kind) => kind === 'customer_credit';
/** The Portuguese label a screen shows. UI text is Portuguese; code is not. */
export const PAYMENT_METHOD_KIND_LABELS = {
    cash: 'Dinheiro',
    pix: 'PIX',
    card_credit: 'Cartão de crédito',
    card_debit: 'Cartão de débito',
    bank_slip: 'Boleto',
    customer_credit: 'Crédito do cliente',
    bank_transfer: 'Transferência bancária',
    tef: 'TEF',
    gateway: 'Gateway de pagamento',
    voucher: 'Vale troca',
};
/**
 * The set a Brazilian shop starts with.
 *
 * Not a migration and not a database default: it is offered to a tenant that
 * has none, and it is ordinary shop configuration from that moment on. A shop
 * that does not take boleto archives it; a shop that only takes PIX ends up
 * with one row. Seeding these silently at tenant creation would be the same
 * data with none of that visible.
 *
 * The order is the order a counter uses them in, which is roughly how often.
 */
export const DEFAULT_PAYMENT_METHODS = [
    { code: 'DINHEIRO', name: 'Dinheiro', kind: 'cash', maxInstalments: 1 },
    { code: 'PIX', name: 'PIX', kind: 'pix', maxInstalments: 1 },
    {
        code: 'DEBITO',
        name: 'Cartão de débito',
        kind: 'card_debit',
        maxInstalments: 1,
    },
    // Twelve, because "12x sem juros" is the default expectation of a Brazilian
    // shopper and a credit method capped at one instalment is a method that
    // cannot do the thing it is for. The fee is deliberately NOT defaulted: the
    // MDR is a negotiated contract, and guessing 3,49% would be inventing one.
    {
        code: 'CREDITO',
        name: 'Cartão de crédito',
        kind: 'card_credit',
        maxInstalments: 12,
    },
    { code: 'BOLETO', name: 'Boleto', kind: 'bank_slip', maxInstalments: 12 },
];
/** `PIX`, `CARTAO-2`, `VALE_ALIMENTACAO`. The same rule pricing uses for a list code. */
const METHOD_CODE = /^[A-Z0-9][A-Z0-9._/-]{0,39}$/;
export const normalizeMethodCode = (value) => value.trim().toUpperCase().replace(/\s+/g, '');
export const isValidMethodCode = (value) => METHOD_CODE.test(value);
// ---------------------------------------------------------------------------
// The payment record
// ---------------------------------------------------------------------------
/**
 * What a payment pays for.
 *
 * A **loose** reference: `documentType` plus `documentId`, and no foreign key.
 * ADR-0018 argues the case in full; the short version is that a payment
 * attaches to a sale today and to a purchase, a standalone receivable and a
 * credit note later, and a foreign key per type means a nullable column per
 * type plus a check constraint nobody maintains.
 *
 * This list is therefore load-bearing in a way a normal enum is not: with no
 * foreign key, the check constraint in migration 0012 is the *only* thing
 * standing between a typo and an orphaned payment. Adding a value is a
 * migration and a deliberate act.
 */
export const PAYMENT_DOCUMENT_TYPES = [
    'sale',
    'purchase',
    'receivable',
    'credit_note',
    /**
     * Money received against nothing yet: an advance, a deposit, a counter
     * payment taken before the document exists.
     *
     * The only type whose `documentId` is null, and the only one the product can
     * produce on its own today.
     */
    'standalone',
];
export const isPaymentDocumentType = (value) => typeof value === 'string' &&
    PAYMENT_DOCUMENT_TYPES.includes(value);
/**
 * Which way the money went.
 *
 * `amount` is always positive and this carries the sign, for the reason the
 * stock ledger gives: one column holding both `-50` and `50` means every
 * aggregate, constraint and report has to remember which it is looking at, and
 * `sum(amount)` silently becomes a net figure that reads like a takings total.
 */
export const PAYMENT_DIRECTIONS = ['in', 'out'];
/**
 * The state of *this row's* money, as it was known when the row was written.
 *
 * Immutable, like every other column. `settled` and `pending` differ by where
 * the money is, not by whether the customer has paid: a credit-card sale is
 * paid at the counter and pending at the acquirer, and both statements are
 * true at once. `failed` records an attempt that did not take, because a
 * declined card that leaves no trace is a card nobody can prove was tried.
 *
 * `refunded` is what a **refund row** carries. It is not a status a receipt
 * acquires — a receipt cannot acquire anything, because it can never be
 * updated. Whether a receipt has been refunded is derived from the rows that
 * name it, which is a fact rather than a column somebody has to keep true.
 */
export const PAYMENT_STATUSES = [
    'pending',
    'settled',
    'refunded',
    'failed',
];
export const isPaymentStatus = (value) => typeof value === 'string' &&
    PAYMENT_STATUSES.includes(value);
// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------
/** R$ 0,00, at the scale the `amount` column carries. */
export const ZERO_AMOUNT = zero(MONEY_SCALE);
/** 0%, at the scale the `fee_percent` column carries. */
export const ZERO_PERCENT = zero(PERCENT_SCALE);
/**
 * Reads an amount from a string.
 *
 * A string, always: `numeric` arrives from the pg driver as text (see
 * `packages/database/src/client.ts`) and travels the wire as text, precisely so
 * that no boundary can round it. Throws rather than returning null, because
 * every caller of this one is reading a value that has already been validated
 * — a column, or a body Zod has checked.
 */
export const parseAmount = (value) => round(fromString(value.trim()), MONEY_SCALE, 'half-up');
export const parsePercent = (value) => round(fromString(value.trim()), PERCENT_SCALE, 'half-up');
/** The wire and database format. Never a JSON number. */
export const amountToWire = (value) => toString(value);
/**
 * A payment amount must be strictly positive.
 *
 * Zero is not a payment — it is a row claiming money moved when none did.
 * Negative is refused because `direction` carries the sign.
 */
export const isValidPaymentAmount = (value) => isPositive(value);
export const isSameAmount = (left, right) => compare(left, right) === 0;
//# sourceMappingURL=entities.js.map